import { useCallback, useEffect, useState, type ElementType } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, Link2, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';
import api from '@/api';
import {
  fetchTeamChatWebhook,
  testTeamChatWebhook,
  updateTeamChatWebhook,
} from '@/api/teamChat';
import { notifications } from '@/utils/notifications';

type SettingsBlockProps = {
  icon: ElementType;
  title: string;
  description?: string;
  children: React.ReactNode;
};

function SettingsBlock({ icon: Icon, title, description, children }: SettingsBlockProps) {
  return (
    <section className="space-y-4">
      <header className="flex items-baseline gap-3">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
          <Icon size={16} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-bold text-foreground">{title}</h3>
          {description ? (
            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{description}</p>
          ) : null}
        </div>
      </header>
      <div className="rounded-2xl border border-border bg-card p-5 sm:p-6">{children}</div>
    </section>
  );
}

export function TeamChatSettings({ embedded = true }: { embedded?: boolean }) {
  const { t } = useTranslation('common');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [notifyDefault, setNotifyDefault] = useState<'always' | 'never' | 'ask'>('ask');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [wh, prefRes] = await Promise.all([
        fetchTeamChatWebhook(),
        api.get<{ status: string; preference?: { notifyTeamDefault?: 'always' | 'never' | 'ask' } }>(
          '/preference'
        ),
      ]);
      setWebhookUrl(wh?.url ?? '');
      setWebhookEnabled(wh?.enabled ?? false);
      if (prefRes.data?.preference?.notifyTeamDefault) {
        setNotifyDefault(prefRes.data.preference.notifyTeamDefault);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const saveWebhook = async () => {
    setSaving(true);
    try {
      await updateTeamChatWebhook({
        url: webhookUrl.trim() || undefined,
        enabled: webhookEnabled,
      });
      notifications.success(t('teamChat.webhook.saved'));
    } catch {
      notifications.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const saveNotifyDefault = async (value: 'always' | 'never' | 'ask') => {
    setNotifyDefault(value);
    try {
      await api.put('/preference', { notifyTeamDefault: value });
      notifications.success(t('common.success'));
    } catch {
      notifications.error(t('common.error'));
    }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      await saveWebhook();
      const res = await testTeamChatWebhook();
      if (res.ok) notifications.success(t('common.success'));
      else notifications.error(res.message ?? t('common.error'));
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }

  const wrapperClass = embedded ? 'space-y-6' : 'mx-auto max-w-xl space-y-8 px-4 py-8';

  return (
    <div id="team-chat-settings" className={wrapperClass}>
      <SettingsBlock icon={Bell} title={t('teamChat.settings.notifyDefault')}>
        <select
          value={notifyDefault}
          onChange={(e) => void saveNotifyDefault(e.target.value as 'always' | 'never' | 'ask')}
          className="w-full rounded-xl border border-border bg-muted/20 px-3 py-2.5 text-sm font-medium text-foreground focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/10"
        >
          <option value="ask">{t('teamChat.settings.notifyDefaultAsk')}</option>
          <option value="always">{t('teamChat.settings.notifyDefaultAlways')}</option>
          <option value="never">{t('teamChat.settings.notifyDefaultNever')}</option>
        </select>
      </SettingsBlock>

      <SettingsBlock
        icon={Link2}
        title={t('teamChat.webhook.title')}
        description={t('teamChat.webhook.description')}
      >
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/80">
              {t('teamChat.webhook.urlLabel')}
            </label>
            <input
              type="url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://hooks.slack.com/..."
              className="w-full rounded-xl border border-border bg-muted/20 px-3 py-2.5 text-sm font-medium text-foreground focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/10"
            />
          </div>
          <label className="flex items-center gap-2 text-sm font-medium text-foreground">
            <input
              type="checkbox"
              checked={webhookEnabled}
              onChange={(e) => setWebhookEnabled(e.target.checked)}
              className="rounded border-border"
            />
            {t('teamChat.webhook.enabled')}
          </label>
          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              disabled={saving}
              onClick={() => void saveWebhook()}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:opacity-50"
            >
              {t('common.save')}
            </button>
            <button
              type="button"
              disabled={testing || !webhookUrl.trim()}
              onClick={() => void runTest()}
              className={clsx(
                'rounded-xl border border-border px-4 py-2 text-sm font-bold text-foreground',
                'hover:bg-muted/40 disabled:opacity-50'
              )}
            >
              {t('teamChat.webhook.test')}
            </button>
          </div>
        </div>
      </SettingsBlock>
    </div>
  );
}
